import { useState, useEffect, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
// import primarylogo from "../../assets/images/primarylogo.png";
import newlogo from "../../assets/images/newlogohorizontal.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faReceipt,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";

export const ScrollNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const history = useHistory();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`sticky top-0 left-0 w-full z-[9999] transition-all duration-300 py-2 ${
        isScrolled
          ? "bg-white/80 text-black shadow-lg backdrop-blur-md"
          : "bg-transparent text-black"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0">
            <Link to="/">
              <img src={newlogo} alt="Fusion Cabs" className="h-12" />
            </Link>
          </div>
          <div className="hidden md:block">
            <ul className="flex items-center gap-4">
              <li>
                <Link to="/" className="hover:text-gray-300">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-gray-300">
                  About
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-gray-300">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-gray-300">
                  Contact
                </Link>
              </li>
              {isAuthenticated ? (
                <li className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-2 focus:outline-none"
                  >
                    <span>{user?.name}</span>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <AnimatePresence>
                    {isDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.1, ease: "easeOut" }}
                        className="absolute right-0 mt-3 w-60 origin-top-right rounded-lg bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-20"
                      >
                        <div className="py-1">
                          <div className="px-4 py-3">
                            <p className="text-sm font-semibold text-gray-900">
                              Signed in as
                            </p>
                            <p className="truncate text-sm font-medium text-gray-700">
                              {user?.name}
                            </p>
                          </div>
                          <div className="border-t border-gray-200" />
                          <div className="p-1">
                            <Link
                              to="/bookings"
                              onClick={() => setIsDropdownOpen(false)}
                              className="group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                            >
                              <FontAwesomeIcon
                                icon={faReceipt}
                                className="h-5 w-5 text-gray-400 group-hover:text-gray-500"
                              />
                              My Bookings
                            </Link>
                            <button
                              onClick={handleLogout}
                              className="group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                            >
                              <FontAwesomeIcon
                                icon={faRightFromBracket}
                                className="h-5 w-5 text-gray-400 group-hover:text-gray-500"
                              />
                              Logout
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              ) : (
                <li>
                  <Link
                    to="/signup"
                    className="bg-black text-white px-6 py-2 rounded-md font-medium hover:bg-gray-800 transition-colors duration-300"
                  >
                    Sign Up
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-black focus:outline-none"
              aria-label="Toggle navigation menu"
            >
              <svg
                className={`${isMobileMenuOpen ? "hidden" : "block"} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <svg
                className={`${isMobileMenuOpen ? "block" : "hidden"} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:hidden bg-white border-t border-gray-100 shadow-lg`}
      >
        <ul className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <li>
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 text-gray-800 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors duration-300"
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/about"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 text-gray-800 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors duration-300"
            >
              About
            </Link>
          </li>
          <li>
            <Link
              to="/services"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 text-gray-800 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors duration-300"
            >
              Services
            </Link>
          </li>
          <li>
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 text-gray-800 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors duration-300"
            >
              Contact
            </Link>
          </li>
          {isAuthenticated ? (
            <>
              <div className="border-t border-gray-200 my-1"></div>
              <li className="px-3 py-2 text-sm text-gray-500">
                Signed in as{" "}
                <span className="font-semibold text-gray-900">
                  {user?.name}
                </span>
              </li>
              <li>
                <Link
                  to="/bookings"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 text-gray-800 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors duration-300"
                >
                  My Bookings
                </Link>
              </li>
              <li>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-3 py-2 text-gray-800 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors duration-300"
                >
                  Logout
                </button>
              </li>
            </>
          ) : (
            <li>
              <Link
                to="/signup"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 text-blue-800 font-medium hover:bg-gray-50 rounded-md transition-colors duration-300"
              >
                Sign Up
              </Link>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
};
